<script setup lang="ts">
import head from 'lodash-es/head';
import type { OptionsModel } from '../../pages/cms/management/websites/_runtime/services/WebsitesService';
import WebsitesService from '../../pages/cms/management/websites/_runtime/services/WebsitesService';
import { useWebsitesStore } from '../../pages/cms/management/websites/_runtime/stores/websites';

const props = withDefaults(defineProps<{
    disabled?: boolean;
}>(), {
    disabled: false
});

const emit = defineEmits<{
    (e: 'change-website', value: number): void;
    (e: 'change-locale', value: string): void;
}>();

const { $log } = useLogging();
const store = useWebsitesStore();

const websites = computed({
    get(): OptionsModel[]
    {
        return store.options;
    },
    set(options: OptionsModel[])
    {
        store.options = options;
    }
});

const website = computed<OptionsModel>(() =>
{
    return head(websites.value.filter(p => p.id == current.value.website || 0));
});

const locales = computed<Option[]>(() =>
{
    const site = website.value || { locales: [] } as OptionsModel;

    return site.locales.map((p) =>
    {
        return { value: p, text: p.toUpperCase() };
    });
});

const current = computed<CurrentWebsite>(() =>
{
    return store.current;
});

async function loadWebsites(): Promise<void>
{
    try
    {
        if (websites.value.length == 0)
        {
            websites.value = await WebsitesService.getOptions();

            if (websites.value.length > 0)
            {
                if (!current.value.website)
                    current.value.website = websites.value[0].id;

                if (!current.value.locale)
                    current.value.locale = head(websites.value[0].locales) || null;
            }
        }
    }
    catch (ex)
    {
        websites.value = [];
        current.value.website = null;
        current.value.locale = null;
        $log.debug(ex);
    }
}

function handleCurrentWebsiteChange(id: string): number
{
    try
    {
        return (current.value.website = Number(id));
    }
    catch (ex)
    {
        $log.debug(ex);
    }
}

function onWebsiteChanged(value: string): void
{
    const website = handleCurrentWebsiteChange(value);

    current.value.locale = head(locales.value).value || null;

    emit('change-website', website);
}

function onLocaleChanged(value: string): void
{
    emit('change-locale', value);
}

onBeforeMount(async (): Promise<void> =>
{
    await loadWebsites();
});
</script>

<template>
    <div class="d-flex">
        <IdeoFormSelect
            v-model="current.website"
            name="website"
            class="me-2 flex-fill"
            :options="websites"
            value-field="id"
            text-field="title"
            :disabled="websites.length === 0 || props.disabled"
            @update:model-value="onWebsiteChanged"
        />
        <IdeoFormSelect
            v-model="current.locale"
            name="locales"
            :options="locales"
            value-field="value"
            text-field="text"
            :disabled="locales.length === 0 || props.disabled"
            @update:model-value="onLocaleChanged"
        />
    </div>
</template>
